import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { get } from '../../services/apiService';

const formatUtc = (value) => (value ? new Date(value).toISOString().replace('T', ' ').slice(0, 19) : '');

const csvCell = (value) => {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvLine = (cells) => cells.map(csvCell).join(',');

const BUG_COLUMNS = [
  'ADO ID', 'Title', 'Priority', 'State', 'State Counts As Closed', 'Team', 'Environment', 'Issue Source',
  'Created (UTC)', 'Closed (UTC)', 'Resolved (UTC)', 'Close Date Used', 'Resolution Days (raw)', 'Resolution Days (rounded)'
];

const bugCells = (bug) => [
  bug.id, bug.title, bug.priority, bug.state, bug.stateCountsAsClosed ? 'Yes' : 'No', bug.team, bug.environment,
  bug.issueSource, formatUtc(bug.createdDate), formatUtc(bug.closedDate), formatUtc(bug.resolutionDate),
  bug.closeDateUsed || '', bug.resolutionDaysRaw ?? '', bug.resolutionDaysRounded ?? ''
];

const buildCsv = (details) => {
  const { summary, window: win } = details;
  const lines = [
    csvLine(['Average Resolution Time - validation export']),
    csvLine(['Month', details.month]),
    csvLine(['Chart window start (UTC)', formatUtc(win.start)]),
    csvLine(['Chart window end (UTC)', formatUtc(win.end)]),
    csvLine(['Calendar month start (UTC)', formatUtc(win.calendarStart)]),
    csvLine(['Calendar month end, exclusive (UTC)', formatUtc(win.calendarEnd)]),
    csvLine(['Chart value (avg days, rounded)', summary.chartAvgDays]),
    csvLine(['Unrounded average days', summary.unroundedAvgDays ?? '']),
    csvLine(['Median days', summary.medianDays ?? '']),
    csvLine(['Bugs counted in chart', summary.countedBugs]),
    csvLine(['Bugs closed in month but excluded', summary.excludedBugs]),
    csvLine(['Bugs raised in month', summary.raisedBugs]),
    '',
    csvLine(['SECTION 1: Bugs closed in month and counted in the chart']),
    csvLine(BUG_COLUMNS),
    ...details.resolved.map((bug) => csvLine(bugCells(bug))),
    '',
    csvLine(['SECTION 2: Bugs closed in month but NOT counted in the chart']),
    csvLine([...BUG_COLUMNS, 'Excluded Reason']),
    ...details.excluded.map((bug) => csvLine([...bugCells(bug), bug.excludedReason])),
    '',
    csvLine(['SECTION 3: Bugs raised (created) in month']),
    csvLine([...BUG_COLUMNS, 'Counted In Created vs Resolved Chart']),
    ...details.raised.map((bug) => csvLine([...bugCells(bug), bug.countedInCreatedVsResolvedChart ? 'Yes' : 'No']))
  ];
  const eol = String.fromCharCode(13, 10);
  return String.fromCharCode(0xFEFF) + lines.join(eol);
};

const downloadCsv = (csv, fileName) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ResolutionTimeDetailsDialog = ({ open, onClose, months, initialMonthKey, getAdoWorkItemUrl }) => {
  const [monthKey, setMonthKey] = useState(initialMonthKey || '');
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open) {
      setMonthKey(initialMonthKey || '');
      setTab(0);
    }
  }, [open, initialMonthKey]);

  useEffect(() => {
    if (!open || !monthKey) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDetails(null);
    get(`/api/workitems/production-bugs/public/resolution-time-details?month=${encodeURIComponent(monthKey)}`)
      .then((response) => {
        if (cancelled) return;
        if (response && response.success) setDetails(response.data);
        else setError((response && response.message) || 'Failed to load details');
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load details');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, monthKey]);

  const rowsByTab = details ? [details.resolved, details.excluded, details.raised] : [[], [], []];
  const rows = rowsByTab[tab];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Average Resolution Time - bug details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="rt-details-month">Month</InputLabel>
            <Select
              labelId="rt-details-month"
              label="Month"
              value={monthKey}
              onChange={(e) => setMonthKey(e.target.value)}
            >
              {months.map((m) => (
                <MenuItem key={m.monthKey} value={m.monthKey}>{m.month}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {details && (
            <Typography variant="body2" color="text.secondary">
              Chart value: <strong>{details.summary.chartAvgDays} days</strong> · unrounded {details.summary.unroundedAvgDays ?? '-'} · median {details.summary.medianDays ?? '-'} · {details.summary.countedBugs} counted · {details.summary.excludedBugs} excluded · {details.summary.raisedBugs} raised
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

        {details && !loading && (
          <>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
              <Tab label={`Closed & counted (${details.resolved.length})`} />
              <Tab label={`Closed but excluded (${details.excluded.length})`} />
              <Tab label={`Raised in month (${details.raised.length})`} />
            </Tabs>
            {rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No bugs in this list.</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 460, border: '1px solid', borderColor: 'divider' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>ADO ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created (UTC)</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Closed / Resolved (UTC)</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Days (raw / rounded)</TableCell>
                      {tab === 1 && <TableCell sx={{ fontWeight: 700 }}>Excluded Reason</TableCell>}
                      {tab === 2 && <TableCell sx={{ fontWeight: 700 }}>In Created vs Resolved chart</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((bug) => (
                      <TableRow key={bug.id} hover>
                        <TableCell>
                          <Link href={getAdoWorkItemUrl(bug.id)} target="_blank" rel="noopener noreferrer">{bug.id}</Link>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Typography variant="body2" noWrap title={bug.title}>{bug.title}</Typography>
                        </TableCell>
                        <TableCell>{bug.priority}</TableCell>
                        <TableCell>{bug.state}</TableCell>
                        <TableCell>{formatUtc(bug.createdDate)}</TableCell>
                        <TableCell>
                          {formatUtc(bug.closureDate)}
                          {bug.closeDateUsed === 'resolutionDate' && ' (resolved)'}
                        </TableCell>
                        <TableCell align="right">
                          {bug.resolutionDaysRaw != null ? `${bug.resolutionDaysRaw} / ${bug.resolutionDaysRounded}` : ''}
                        </TableCell>
                        {tab === 1 && <TableCell>{bug.excludedReason}</TableCell>}
                        {tab === 2 && <TableCell>{bug.countedInCreatedVsResolvedChart ? 'Yes' : 'No'}</TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          disabled={!details || loading}
          onClick={() => downloadCsv(buildCsv(details), `resolution-time-${details.month}.csv`)}
        >
          Export CSV
        </Button>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResolutionTimeDetailsDialog;
